import DashboardContentReportsV3 from "./DashboardContentReportsV3";
import DashboardHeaderReportsV3 from "./DashboardHeaderReportsV3";
import DashboardReportsV3 from "./DashboardReportsV3";
import MultiTableReportEditor from "./MultiTableReportEditor";
import ReportBuilder from "./ReportBuilder";
import ReportResults from "./ReportResults";
import ReportsIndex from "./ReportsIndex";
import { PageLoader } from "./components/LoadingOverlay";
import NameDialog from "./components/NameDialog";
import ReportsListError from "./components/ReportsListError";
import { useDuplicateReport } from "./hooks/useDuplicateReport";
import { useImportReport } from "./hooks/useImportReport";
import { useLastOpenedReport } from "./hooks/useLastOpenedReport";
import { useReportBrowserTitle } from "./hooks/useReportBrowserTitle";
import { useReportDialog } from "./hooks/useReportDialog";
import { useReportExecution } from "./hooks/useReportExecution";
import { ReportExportProvider } from "./hooks/useReportExportContext";
import { ReportStateProvider } from "./hooks/useReportState";
import { Close as CloseIcon } from "@mui/icons-material";
import { Alert, IconButton, Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { useCallback, useEffect, useMemo } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import * as reportsV3Service from "services/reportsV3Service";

const DynamicReportViewer = () => {
  const { reportId } = useParams();
  const { rememberOpened } = useLastOpenedReport();
  useEffect(() => {
    rememberOpened(reportId);
  }, [reportId, rememberOpened]);
  if (!reportId) {
    return (
      <Stack
        spacing={0}
        sx={{
          width: "100%",
          p: 4,
        }}
      >
        <Alert severity={"error"}>{"Report ID not found"}</Alert>
      </Stack>
    );
  }
  return <SavedReportViewer key={reportId} reportId={reportId} />;
};

const SavedReportViewer = ({ reportId }) => {
  const {
    reportData,
    allResults,
    selectedResultIndex,
    setSelectedResultIndex,
    error,
    setResults,
    rerun,
    isRerunning,
    isLoading,
    isSummaryLoading,
    summaryResults,
    requestSummary,
    report: reportConfig,
    enhancedColumns,
    reorderColumns,
    applyColumnRenames,
  } = useReportExecution({
    reportId,
  });

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  useEffect(() => {
    if (!error) return;
    enqueueSnackbar(error, {
      variant: "error",
      autoHideDuration: 60000,
      action: (snackbarId) => (
        <IconButton
          size={"small"}
          onClick={() => closeSnackbar(snackbarId)}
          sx={{ color: "inherit" }}
        >
          <CloseIcon fontSize={"small"} />
        </IconButton>
      ),
    });
  }, [error, enqueueSnackbar, closeSnackbar]);

  const getColumnDataType = useCallback(
    (column) => {
      const columnDef = enhancedColumns?.find((c) => c.column === column);
      return columnDef?._dataType;
    },
    [enhancedColumns]
  );

  const sourceTables = reportConfig?.specification?.sourceTables || [
    "review_list",
  ];

  const columnDisplayNameMap = useMemo(() => {
    const map = {};
    (enhancedColumns ?? []).forEach((c) => {
      if (c?.column) {
        map[c.column] = c.displayName ?? c.column;
      }
    });
    return map;
  }, [enhancedColumns]);

  if (isLoading) {
    return <PageLoader />;
  }
  if (!reportConfig) {
    return null;
  }
  return (
    <ReportStateProvider
      reportId={reportId}
      savedReport={reportConfig}
      sourceTables={sourceTables}
      getColumnDataType={getColumnDataType}
      initialColumnsData={enhancedColumns}
      onReorderColumns={reorderColumns}
      columnDisplayNameMap={columnDisplayNameMap}
      selectedResultIndex={selectedResultIndex}
    >
      <ReportResults
        reportData={reportData}
        allResults={allResults}
        selectedResultIndex={selectedResultIndex}
        onResultSelect={setSelectedResultIndex}
        isSummaryLoading={isSummaryLoading}
        summaryResults={summaryResults}
        onRequestSummary={requestSummary}
        columnsData={enhancedColumns}
        reorderColumns={reorderColumns}
        applyColumnRenames={applyColumnRenames}
        savedReport={reportConfig}
        onResultsUpdate={setResults}
        rerun={rerun}
        isRerunning={isRerunning}
        executionError={error}
      />
    </ReportStateProvider>
  );
};

const ReportsApp = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: savedReports,
    isLoading: reportsLoading,
    isError: reportsListFailed,
    isFetching: reportsFetching,
    refetch: refetchSavedReports,
  } = reportsV3Service.useGetSavedReportsQuery();

  useReportBrowserTitle(savedReports);

  const dialog = useReportDialog(savedReports);
  const importReport = useImportReport(savedReports);
  const duplicateReport = useDuplicateReport(savedReports);

  const { handleOpenEditReportDialog } = dialog;

  const handleNewReport = useCallback(() => {
    navigate("/report-builder/new");
  }, [navigate]);

  const handleCreateMultiTable = useCallback(() => {
    navigate("/report-builder/multi-table/new");
  }, [navigate]);

  const isOnNewReportRoute = location.pathname === "/report-builder/new";
  const isOnNewMultiTableRoute =
    location.pathname === "/report-builder/multi-table/new";

  const sortedReports = useMemo(
    () =>
      [...(savedReports ?? [])].sort((a, b) => {
        return new Date(b.updated).getTime() - new Date(a.updated).getTime();
      }),
    [savedReports]
  );

  const tabs = useMemo(() => {
    const savedReportTabs = sortedReports.map((report) => ({
      title: report.displayName,
      path: report.internalName,
      description: report.description,
      specification: undefined,
      hasDropdown: true,
      locked: report.canWrite === false,
      allUsers: Boolean(report.allUsers),
      multiOutputReport: Boolean(report.multiOutputReport),
    }));
    const newReportTab = isOnNewReportRoute
      ? { title: "New Report (unsaved)", path: "new" }
      : null;
    const newMultiTableTab = isOnNewMultiTableRoute
      ? { title: "New Multi Table Report (unsaved)", path: "multi-table/new" }
      : null;
    const newReportButton = {
      title: "+ New Report",
      path: "#",
      rightAlign: true,
      onClick: handleNewReport,
    };
    return [
      ...savedReportTabs,
      ...(newReportTab ? [newReportTab] : []),
      ...(newMultiTableTab ? [newMultiTableTab] : []),
      newReportButton,
    ];
  }, [
    sortedReports,
    handleNewReport,
    isOnNewReportRoute,
    isOnNewMultiTableRoute,
  ]);
  const handleToggleExpand = useCallback(
    (tabPath) => {
      handleOpenEditReportDialog(tabPath);
    },
    [handleOpenEditReportDialog]
  );

  const handleEditDialogReport = useCallback(() => {
    const reportId = dialog.dialogReportId;
    if (!reportId) return;
    const report = savedReports?.find((r) => r.internalName === reportId);
    if (report?.multiOutputReport) {
      dialog.handleDialogClose();
      navigate(`/report-builder/multi-table/${reportId}/edit`);
    } else {
      dialog.handleEditReport();
    }
  }, [dialog, savedReports, navigate]);

  const viewportFillAppStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 37px)",
    overflow: "visible",
  };
  if (reportsLoading) {
    return (
      <ReportExportProvider>
        <div className={"app"} style={viewportFillAppStyle}>
          <input
            type={"file"}
            ref={importReport.fileInputRef}
            accept={".json"}
            onChange={importReport.handleFileSelected}
            style={{ display: "none" }}
          />
          <DashboardReportsV3 viewportFill>
            <DashboardHeaderReportsV3
              tabs={[]}
              isFullWidth
              topNavBorderless
              sticky
              viewportFill
              onImport={importReport.handleImportClick}
              onCreate={handleNewReport}
              onCreateMultiTable={handleCreateMultiTable}
            />
            <DashboardContentReportsV3 isFullWidth viewportFill>
              <PageLoader />
            </DashboardContentReportsV3>
          </DashboardReportsV3>
          <NameDialog
            open={importReport.dialogOpen}
            onClose={importReport.handleDialogClose}
            onSave={importReport.handleImportConfirm}
            reportName={importReport.importName}
            onReportNameChange={importReport.setImportName}
            reportDescription={importReport.importDescription}
            onReportDescriptionChange={importReport.setImportDescription}
            error={importReport.importError}
            onErrorClear={importReport.clearImportError}
            isSaving={importReport.isImporting}
            isSaveDisabled={!importReport.isNameUnique}
            title={"Import Report"}
            saveButtonLabel={"Import"}
            savingButtonLabel={"Importing..."}
          />
        </div>
      </ReportExportProvider>
    );
  }
  return (
    <ReportExportProvider>
      <div className={"app"} style={viewportFillAppStyle}>
        <input
          type={"file"}
          ref={importReport.fileInputRef}
          accept={".json"}
          onChange={importReport.handleFileSelected}
          style={{ display: "none" }}
        />
        <DashboardReportsV3 viewportFill>
          <DashboardHeaderReportsV3
            tabs={tabs}
            isFullWidth
            topNavBorderless
            sticky
            onToggleExpand={handleToggleExpand}
            onDuplicate={duplicateReport.handleDuplicate}
            viewportFill
            onImport={importReport.handleImportClick}
            onCreate={handleNewReport}
            onCreateMultiTable={handleCreateMultiTable}
          />
          <DashboardContentReportsV3 isFullWidth viewportFill>
            {reportsListFailed && (
              <ReportsListError
                onRetry={refetchSavedReports}
                isRetrying={reportsFetching}
              />
            )}
            <Routes>
              <Route
                path={"new"}
                element={<ReportBuilder key={location.key} embedded />}
              />
              <Route
                path={"/:reportId/edit"}
                element={<ReportBuilder key={location.pathname} />}
              />
              <Route
                path={"multi-table/new"}
                element={<MultiTableReportEditor />}
              />
              <Route
                path={"multi-table/:reportId/edit"}
                element={<MultiTableReportEditor />}
              />
              <Route path={"/:reportId/*"} element={<DynamicReportViewer />} />
              <Route
                index
                element={
                  <ReportsIndex
                    reports={sortedReports}
                    onCreate={handleNewReport}
                  />
                }
              />
            </Routes>
          </DashboardContentReportsV3>
          <NameDialog
            open={dialog.dialogOpen && dialog.dialogMode === "edit"}
            onClose={dialog.handleDialogClose}
            onSave={dialog.handleDialogSave}
            onEdit={
              dialog.dialogMode === "edit" ? handleEditDialogReport : undefined
            }
            reportName={dialog.dialogReportName}
            onReportNameChange={dialog.setDialogReportName}
            reportDescription={dialog.dialogReportDescription}
            onReportDescriptionChange={dialog.setDialogReportDescription}
            originalReportName={dialog.originalReportName}
            originalReportDescription={dialog.originalReportDescription}
            error={dialog.dialogError}
            onErrorClear={dialog.clearDialogError}
            isSaving={dialog.isUpdatingReport}
            title={
              dialog.dialogMode === "edit" ? "Edit Report" : "Create New Report"
            }
            saveButtonLabel={dialog.dialogMode === "edit" ? "Save" : "Create"}
            savingButtonLabel={
              dialog.dialogMode === "edit" ? "Saving..." : "Creating..."
            }
            onDelete={
              dialog.dialogMode === "edit"
                ? dialog.handleDeleteReport
                : undefined
            }
            isDeleting={dialog.isDeletingReport}
            isReadOnly={dialog.selectedReportIsReadOnly}
            reportId={dialog.dialogReportId}
            allUsers={dialog.reportMetadata?.allUsers ?? false}
            readOnly={dialog.reportMetadata?.readOnly ?? false}
            canWriteMetadata={dialog.reportMetadata?.canWriteMetadata ?? false}
            onShareSettingsChange={dialog.handleShareSettingsChange}
            isSavingShare={dialog.isUpdatingMetadata}
            shareError={dialog.shareError}
            onShareErrorClear={dialog.clearShareError}
          />
          <NameDialog
            open={importReport.dialogOpen}
            onClose={importReport.handleDialogClose}
            onSave={importReport.handleImportConfirm}
            reportName={importReport.importName}
            onReportNameChange={importReport.setImportName}
            reportDescription={importReport.importDescription}
            onReportDescriptionChange={importReport.setImportDescription}
            error={importReport.importError}
            onErrorClear={importReport.clearImportError}
            isSaving={importReport.isImporting}
            isSaveDisabled={!importReport.isNameUnique}
            title={"Import Report"}
            saveButtonLabel={"Import"}
            savingButtonLabel={"Importing..."}
          />
          <NameDialog
            open={duplicateReport.dialogOpen}
            onClose={duplicateReport.handleDialogClose}
            onSave={duplicateReport.handleImportConfirm}
            reportName={duplicateReport.importName}
            onReportNameChange={duplicateReport.setImportName}
            reportDescription={duplicateReport.importDescription}
            onReportDescriptionChange={duplicateReport.setImportDescription}
            error={duplicateReport.importError}
            onErrorClear={duplicateReport.clearImportError}
            isSaving={duplicateReport.isImporting}
            isSaveDisabled={!duplicateReport.isNameUnique}
            title={"Duplicate report"}
            saveButtonLabel={"Duplicate"}
            savingButtonLabel={"Duplicating..."}
          />
        </DashboardReportsV3>
      </div>
    </ReportExportProvider>
  );
};
export default ReportsApp;
